import * as PIXI from "pixi.js";

import { Vector } from "./vector";
import { Point } from "../models";

import { Common } from "./common";
import { CurrencyPipe } from "../../../shared/utils/currency.pipe";
import { CurrencyDeviderPipe } from "../../../shared/utils/currency-devider.pipe";
import { Table } from "./table";
import { CurrencyInfo } from "../../../shared/models/currency-info";

export type ElementsPositions = "right" | "left";

const ElementsPositions = {
  right: {
    money: { x: 74, y: 17 } as Point, // left-center
    chip: { x: 22, y: -35 } as Point, // top-left
    convertedMoney: { x: 12, y: 30 } as Point, // left-center
  },
  left: {
    money: { x: 74, y: 17 } as Point, // right-center
    chip: { x: 22, y: -35 } as Point, // top-left
    convertedMoney: { x: 55, y: 11 } as Point, // left-center
  },
};

type PotAmountItem = {
  amount: number;
  howManyTimes: number;
  label: string;
};

const availableAmounts = [
  { amount: 100000000, label: "100m" }, // 100m
  { amount: 10000000, label: "10m" }, // 10m
  { amount: 5000000, label: "5m" }, // 5m
  { amount: 1000000, label: "1m" }, // 1m
  { amount: 500000, label: "500k" }, // 500k
  { amount: 100000, label: "100k" }, // 100k
  { amount: 50000, label: "50k" }, // 50k
  { amount: 10000, label: "10k" }, // 10k
  { amount: 5000, label: "5k" }, // 5k
  { amount: 1000, label: "1k" }, // 1k
  { amount: 500, label: "500" }, // 500
  { amount: 100, label: "100" }, // 100
  { amount: 50, label: "50" }, // 50
  { amount: 10, label: "10" }, // 10
  { amount: 5, label: "5" }, // 5
  { amount: 1, label: "1" }, // 1
  { amount: 0.5, label: "50c" }, // 50c
  { amount: 0.1, label: "10c" }, // 10c
  { amount: 0.05, label: "5c" }, // 5c
  { amount: 0.01, label: "1c" }, // 1c
];

export class Pot {
  container;
  private containerSize: Point = { x: 98, y: 41 };
  private moneyTextStyle = new PIXI.TextStyle({
    fontFamily: "Saira Semi Condensed",
    fontSize: 28,
    fontWeight: "500",
    fill: "#ffffff",
  });
  private moneyTextStyleAdvanced = new PIXI.TextStyle({
    fontFamily: "Saira Semi Condensed",
    fontSize: 28,
    fontWeight: "500",
    fill: "#00ff24",
  });
  private convertedMoneyTextStyle = new PIXI.TextStyle({
    fontFamily: "Saira Semi Condensed",
    fontSize: 28,
    fontWeight: "500",
    fill: "#ffffff",
  });
  private chipsSprite: PIXI.Sprite;
  potId: number; // this will be playerId if pot belongs to player
  private potAmount: PIXI.Text;
  private potBackground: PIXI.Graphics;
  private amountContainer: PIXI.Container;
  private convertedPotAmount: PIXI.Text;
  amount: number;
  shouldMove = false;
  targetPosition: Point;
  private movingSpeed = 0;
  private movingDirection: Point;
  defaultPosition: Point;
  private currentPosition: Point;
  private timeToTraget = 0.5; // traveling time
  private oldDistance = 9999;
  isSplit;
  rakeAmount = 0;
  currency: CurrencyInfo;
  seatNumber: number;
  positionOption: string;
  currentPotItems: PotAmountItem[] = [];
  currentPotSprites: PIXI.Sprite[] = [];

  private bigBlind: number;
  private stacksInBB: boolean;
  newChipsDesign: boolean = false;
  showPotWithRakeAmount: boolean;

  constructor(
    textures,
    potId: number,
    position: Point,
    amount: number,
    currency: CurrencyInfo,
    seatNumber: number,

    bigBlind: number,
    stacksInBB: boolean,
    showPotWithRakeAmount: boolean,
    isSplit?: boolean,

    public table?: Table,
    newChipsDesign?: boolean,
  ) {
    this.bigBlind = bigBlind;
    this.stacksInBB = stacksInBB;

    this.seatNumber = seatNumber;
    this.container = new PIXI.Container();
    this.newChipsDesign = newChipsDesign;
    this.defaultPosition = {
      x: position.x - this.containerSize.x / 2,
      y: position.y - this.containerSize.y / 2,
    };
    this.currentPosition = {
      x: this.defaultPosition.x,
      y: this.defaultPosition.y,
    };
    this.container.position.set(this.defaultPosition.x, this.defaultPosition.y);

    this.chipsSprite = new PIXI.Sprite(textures["chip"].texture);
    // this.container.addChild(this.chipsSprite);

    if (amount === undefined) {
      amount = 0;
    }
    this.amount = amount;

    let moneyStyle;

    moneyStyle = this.moneyTextStyle;

    this.potAmount = new PIXI.Text(
      currency
        ? CurrencyPipe.prototype.transform(
            CurrencyDeviderPipe.prototype.transform(this.amount, currency),
            currency,
          )
        : `${this.amount}`,
      moneyStyle,
    );

    this.convertedPotAmount = new PIXI.Text(
      this.currency
        ? CurrencyPipe.prototype.transform(
            CurrencyDeviderPipe.prototype.transform(this.amount, currency),
            currency,
          )
        : `${this.amount}`,
      this.convertedMoneyTextStyle,
    );
    this.container.addChild(this.convertedPotAmount);
    this.amountContainer = new PIXI.Container();

    this.potAmount.position.set(8, 2);

    const bgWidth = this.potAmount.width + 12;
    const bgHeight = this.potAmount.height + 4;

    this.potBackground = new PIXI.Graphics();
    this.potBackground.beginFill(0x000000, 0.75);
    this.potBackground.drawRoundedRect(0, 0, bgWidth, bgHeight, 8);
    this.potBackground.endFill();

    this.amountContainer.addChild(this.potBackground);
    this.amountContainer.addChild(this.potAmount);
    this.amountContainer.pivot.set(bgWidth / 2, bgHeight / 2);
    this.container.addChild(this.amountContainer);
    this.convertedPotAmount.visible = false; // TODO: show/hide if skin standard/advanced
    this.positionOption = Common.isPotLeftSided(
      seatNumber,
      this.table.getMaxPlayers(),
    )
      ? "left"
      : "right";
    if (Common.isPotLeftSided(seatNumber, this.table.getMaxPlayers())) {
      this.setPositions("left");
    } else {
      this.setPositions("right");
    }

    if (this.amount === 0) {
      this.hidePot();
    }

    this.isSplit = isSplit;
    this.currency = currency;
    this.showPotWithRakeAmount = showPotWithRakeAmount;
    this.updateChipsSprites();
  }
  setNewChipsDesign(newChipsDesign: boolean) {
    console.log("revertnewChipDesignset", newChipsDesign);
    this.newChipsDesign = newChipsDesign;
  }
  update(dt) {
    if (this.shouldMove) {
      this.move(dt);
    }
  }

  updatePosition(position: Point) {
    this.defaultPosition = {
      x: position.x - this.containerSize.x / 2,
      y: position.y - this.containerSize.y / 2,
    };
    this.currentPosition = {
      x: this.defaultPosition.x,
      y: this.defaultPosition.y,
    };
    this.container.position.set(this.defaultPosition.x, this.defaultPosition.y);
  }

  setPositions(position: ElementsPositions) {
    // this.chipsSprite.position.set(
    //   ElementsPositions[position].chip.x,
    //   ElementsPositions[position].chip.y,
    // );
    this.amountContainer.position.set(
      ElementsPositions[position].money.x - this.amountContainer.width / 2,
      ElementsPositions[position].money.y,
    );
    this.convertedPotAmount.position.set(
      ElementsPositions[position].convertedMoney.x,
      ElementsPositions[position].convertedMoney.y,
    );

    if (position === "right") {
      this.convertedPotAmount.anchor.set(0, 0.5);
    } else if (position === "left") {
      this.convertedPotAmount.anchor.set(1, 0.5);
    }
  }

  hidePot() {
    this.container.visible = false;
  }

  showPot() {
    this.container.visible = true;
  }

  updateBigBlind(bigBlind: number) {
    this.bigBlind = bigBlind;
    this.updateAmount(this.amount);
  }

  updateStacksInBB(stacksInBB: boolean) {
    this.stacksInBB = stacksInBB;
    this.updateAmount(this.amount);
  }

  updateAmount(newAmount: number, rakeAmount?: number) {
    this.amount = newAmount;

    if (newAmount === undefined) {
      newAmount = 0;
    }

    if (this.showPotWithRakeAmount) {
      if (rakeAmount) {
        if (newAmount > 0) {
          this.rakeAmount = rakeAmount;
          newAmount += rakeAmount;
        }
      }
      //  else if (newAmount > 0) {
      //     newAmount += this.rakeAmount;
      // }
    }

    let newAmountConverted = 0;
    if (this.stacksInBB) {
      const amount = newAmount / this.bigBlind;
      newAmountConverted = Math.round((amount + Number.EPSILON) * 100) / 100;
    } else {
      newAmountConverted = this.currency
        ? CurrencyDeviderPipe.prototype.transform(newAmount, this.currency)
        : newAmount;
    }

    if (this.stacksInBB) {
      this.potAmount.text = `${newAmountConverted} BB`;
    } else {
      this.potAmount.text = this.currency
        ? CurrencyPipe.prototype.transform(newAmountConverted, this.currency)
        : `${newAmountConverted}`;
    }

    if (newAmount === 0) {
      this.rakeAmount = 0;
    }
    this.updateChipsSprites();
    this.refreshBackgroud();
    this.resetPot();
  }
  refreshBackgroud() {
    this.potBackground.clear();
    this.potBackground.beginFill(0x000000, 0.75);
    this.potBackground.drawRoundedRect(
      0,
      0,
      this.potAmount.width + 12,
      this.potAmount.height + 4,
      8,
    );
    this.amountContainer.position.set(
      ElementsPositions[this.positionOption].money.x -
        this.amountContainer.width / 2 -
        (this.currency ? 0 : 20),
      ElementsPositions[this.positionOption].money.y,
    );
    this.potBackground.endFill();
  }
  revertToOriginalPosition(newChipDesign: boolean = false) {
    this.updateChipsSprites();
  }

  move(dt) {
    this.currentPosition.x +=
      (dt / 1000) * this.movingSpeed * this.movingDirection.x;
    this.currentPosition.y +=
      (dt / 1000) * this.movingSpeed * this.movingDirection.y;
    const newDistance = Vector.prototype.distace(
      this.targetPosition,
      this.currentPosition,
    );
    if (this.oldDistance <= newDistance) {
      // this.shouldMove = false;
      this.amount = 0; // TODO: check if this is working
      this.resetPot();
      // this.hidePot();
      // this.currentPosition = { x: this.defaultPosition.x, y: this.defaultPosition.y };

      if (this.isSplit) {
        this.selfDistruct();
      }

      return;
    }
    this.container.position.set(this.currentPosition.x, this.currentPosition.y);
    this.oldDistance = newDistance;
  }

  resetPot() {
    this.shouldMove = false;
    this.currentPosition = {
      x: this.defaultPosition.x,
      y: this.defaultPosition.y,
    };
    this.container.position.set(this.currentPosition.x, this.currentPosition.y);
    this.oldDistance = 9999;
    if (this.amount === 0) {
      this.hidePot();
    } else {
      this.showPot();
    }
  }

  moveToTarget(targetPosition: Point) {
    if (!this.showPotWithRakeAmount) {
      if (this.rakeAmount > 0) {
        this.amount -= this.rakeAmount;
        this.rakeAmount = 0;
        const newAmountConverted = this.currency
          ? CurrencyDeviderPipe.prototype.transform(this.amount, this.currency)
          : this.amount;
        this.potAmount.text = this.currency
          ? CurrencyPipe.prototype.transform(newAmountConverted, this.currency)
          : `${newAmountConverted}`;
      }
    }
    this.resetPot();
    this.targetPosition = targetPosition;
    const distanceToTarget = Vector.prototype.distace(
      targetPosition,
      this.currentPosition,
    );
    this.movingSpeed = distanceToTarget / this.timeToTraget;
    this.movingDirection = Vector.prototype.direction(
      targetPosition,
      this.currentPosition,
    );
    this.shouldMove = true;
  }

  isUpSided(seatPosition: number) {
    if ((seatPosition >= 0 && seatPosition < 4) || seatPosition === 9) {
      return false;
    } else {
      return true;
    }
  }

  selfDistruct() {
    this.container.parent.removeChild(this.container);
  }

  rearngeElements(position: Point, fakeSeatNumber: number) {
    this.defaultPosition = {
      x: position.x - this.containerSize.x / 2,
      y: position.y - this.containerSize.y / 2,
    };
    this.currentPosition = {
      x: this.defaultPosition.x,
      y: this.defaultPosition.y,
    };
    this.container.position.set(this.defaultPosition.x, this.defaultPosition.y);

    if (Common.isPotLeftSided(fakeSeatNumber, this.table.getMaxPlayers())) {
      this.setPositions("left");
    } else {
      this.setPositions("right");
    }
  }

  getCurrencyBreakdown(amount: number): PotAmountItem[] {
    const result: PotAmountItem[] = [];

    for (const amountItem of availableAmounts) {
      if (amount >= amountItem.amount) {
        const howManyTimes = Math.floor(
          this.roundToTwo(
            this.roundToTwo(amount) / this.roundToTwo(amountItem.amount),
          ),
        );
        if (howManyTimes >= 0) {
          result.push({
            amount: amountItem.amount,
            howManyTimes,
            label: amountItem.label,
          });
          amount = this.roundToTwo(
            amount - this.roundToTwo(howManyTimes * amountItem.amount),
          );
        }
      }
      if (result.length >= 5) break;
    }

    return result;
  }

  roundToTwo(num: number): number {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  createChipsSprites(potItems: PotAmountItem[]): PIXI.Sprite[] {
    const sprites: PIXI.Sprite[] = [];

    potItems.forEach((item, index) => {
      const imageUrl = `assets/chips/chip${item.label}x${item.howManyTimes > 4 ? 4 : item.howManyTimes}.png`;
      const texture = PIXI.Texture.from(imageUrl);
      const chipSprite = new PIXI.Sprite(texture);
      chipSprite.zIndex = potItems.length - index;
      chipSprite.scale.set(0.55);

      sprites.push(chipSprite);
    });

    return sprites;
  }
  updateChipsSprites(): void {
    if (this.currentPotSprites.length > 0) {
      this.currentPotSprites.forEach((item) => {
        this.container.removeChild(item);
      });
    }
    if (this.newChipsDesign) {
      this.container.removeChild(this.chipsSprite);
      this.currentPotItems = this.getCurrencyBreakdown(
        this.currency
          ? CurrencyDeviderPipe.prototype.transform(this.amount, this.currency)
          : this.amount,
      );
      this.currentPotSprites = this.createChipsSprites(this.currentPotItems);
      this.setPotsPosition();
    } else {
      this.chipsSprite.scale.set(1.1);
      this.container.addChild(this.chipsSprite);
      this.chipsSprite.position.set(
        ElementsPositions[this.positionOption].money.x +
          this.amountContainer.width / 2 -
          30,
        ElementsPositions[this.positionOption].chip.y + 30,
      );
      this.refreshBackgroud();
    }
  }

  round(value: number, decimals = 2): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  setPotsPosition() {
    const numOfPots = this.currentPotItems.length;
    var xPos = [0, 0, 0, 0, 0];
    var yPos = [0, 0, 0, 0, 0];
    const standardX = ElementsPositions[this.positionOption].chip.x;
    const standardY = ElementsPositions[this.positionOption].chip.y;
    switch (numOfPots) {
      case 1:
        xPos = [standardX];
        yPos = [standardY - (this.currentPotItems[0].howManyTimes - 1) * 6];
        break;
      case 2:
        xPos = [standardX + 17, standardX - 17];
        yPos = [
          standardY - (this.currentPotItems[0].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[1].howManyTimes - 1) * 6,
        ];
        break;
      case 3:
        xPos = [standardX + 17, standardX - 17, standardX];
        yPos = [
          standardY - (this.currentPotItems[0].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[1].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[2].howManyTimes - 1) * 6 - 30,
        ];
        break;
      case 4:
        xPos = [standardX + 17, standardX - 17, standardX + 34, standardX];
        yPos = [
          standardY - (this.currentPotItems[0].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[1].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[2].howManyTimes - 1) * 6 - 30,
          standardY - (this.currentPotItems[3].howManyTimes - 1) * 6 - 30,
        ];
        break;
      case 5:
        xPos = [
          standardX + 17,
          standardX - 17,
          standardX + 34,
          standardX + 0,
          standardX - 34,
        ];
        yPos = [
          standardY - (this.currentPotItems[0].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[1].howManyTimes - 1) * 6,
          standardY - (this.currentPotItems[2].howManyTimes - 1) * 6 - 30,
          standardY - (this.currentPotItems[3].howManyTimes - 1) * 6 - 30,
          standardY - (this.currentPotItems[4].howManyTimes - 1) * 6 - 30,
        ];
        break;
      default:
        return;
    }
    this.currentPotSprites.reverse().forEach((item, i) => {
      item.position.set(
        xPos[this.currentPotItems.length - i - 1],
        yPos[this.currentPotItems.length - i - 1],
      );
      this.container.addChild(item);
    });
  }
}
